import {
  FUNKIT_NATIVE_TOKEN,
  FUN_HYPERCORE_CHAIN_ID_NUMBER,
  FUN_SOLANA_CHAIN_ID_NUMBER,
  RELAY_BITCOIN_CHAIN_ID_NUMBER,
  RELAY_LIGHTER_CHAIN_ID_NUMBER,
  RELAY_LIGHTER_FUN_ADDRESS_MAP,
  RELAY_LIGHTER_USDC_PERPS,
  RELAY_NATIVE_TOKEN,
  RELAY_NATIVE_TOKEN_BITCOIN,
  RELAY_NATIVE_TOKEN_HYPERCORE,
  RELAY_NATIVE_TOKEN_SOLANA,
  RELAY_SOLANA_CHAIN_ID_NUMBER,
  RELAY_TERMINAL_STATUSES,
} from './constants'
import type { RelayAddress, RelayVmType } from './execution'
import {
  CheckoutRefundState,
  CheckoutState,
  type RelayExecutionInfo,
  RelayExecutionStatus,
} from './types'

/**
 * Converts a token address within Funkit to the corresponding Relay token address.
 * TODO: what is the chainId type here? fun chain id or relay chain id?
 */
export function convertFunToRelayTokenAddress<T extends RelayVmType>(
  address: RelayAddress<T>,
  chainId: number,
): RelayAddress<T> {
  // convert Fun's NATIVE_TOKEN representation
  if (address.toLowerCase() === FUNKIT_NATIVE_TOKEN.toLowerCase()) {
    switch (chainId) {
      case FUN_HYPERCORE_CHAIN_ID_NUMBER:
        return RELAY_NATIVE_TOKEN_HYPERCORE as RelayAddress<T>

      case RELAY_BITCOIN_CHAIN_ID_NUMBER:
        return RELAY_NATIVE_TOKEN_BITCOIN as RelayAddress<T>

      // TODO: is it correct to assume chainId here is relay chain id?
      case RELAY_SOLANA_CHAIN_ID_NUMBER:
      case FUN_SOLANA_CHAIN_ID_NUMBER:
        return RELAY_NATIVE_TOKEN_SOLANA as RelayAddress<T>

      case RELAY_LIGHTER_CHAIN_ID_NUMBER:
        // legacy address for Lighter USDC perps
        return RELAY_LIGHTER_USDC_PERPS as RelayAddress<T>

      default:
        return RELAY_NATIVE_TOKEN as RelayAddress<T>
    }
  }

  if (RELAY_LIGHTER_CHAIN_ID_NUMBER === chainId) {
    const relayAddress =
      RELAY_LIGHTER_FUN_ADDRESS_MAP[
        address.toLowerCase() as keyof typeof RELAY_LIGHTER_FUN_ADDRESS_MAP
      ]
    if (relayAddress) {
      return relayAddress as RelayAddress<T>
    }
  }

  return address
}

/**
 * Converts a chainId within Funkit to the corresponding Relay chainId.
 */
export function convertFunToRelayChainId(chainId: number): number {
  if (chainId === FUN_SOLANA_CHAIN_ID_NUMBER) {
    return RELAY_SOLANA_CHAIN_ID_NUMBER
  }
  return chainId
}

export function getRelayExecutionRefundState(
  info: RelayExecutionInfo,
): CheckoutRefundState | undefined {
  switch (info.status) {
    case RelayExecutionStatus.REFUND:
      return CheckoutRefundState.REFUNDED
    default:
      return undefined
  }
}

export function getRelayExecutionState(
  info: RelayExecutionInfo,
): CheckoutState {
  switch (info.status) {
    case RelayExecutionStatus.SUCCESS:
      return CheckoutState.COMPLETED
    case RelayExecutionStatus.FAILURE:
    case RelayExecutionStatus.REFUND:
      return CheckoutState.CHECKOUT_ERROR
    default:
      return CheckoutState.PENDING_RECEIVAL
  }
}

/**
 * Checks whether the Relay execution has reached a terminal status.
 */
export function isRelayExecutionTerminalStatus(
  info: RelayExecutionInfo,
): boolean {
  return RELAY_TERMINAL_STATUSES.includes(info.status)
}

export type Serializable =
  | bigint
  | boolean
  | null
  | number
  | string
  | Date
  | object // Should be '{ [key: string]: Serializable }' but helper gets called with loosely-typed 'object' and interfaces
  | Serializable[]

export function jsonStringifyWithBigIntSanitization(
  serializable: Serializable,
): string {
  return JSON.stringify(
    serializable,
    (_, value) =>
      typeof value === 'bigint' ? `0x${value.toString(16)}` : value, // return everything else unchanged
  )
}

export function indexBy<Item>(
  items: Item[],
  getKey: (item: Item) => string,
): Record<string, Item> {
  const indexedItems: Record<string, Item> = {}

  for (const item of items) {
    const key = getKey(item)

    if (!key) {
      continue
    }

    indexedItems[key] = item
  }

  return indexedItems
}

export function filterNullishHeaders(
  params: Record<string, string | undefined | null>,
): Record<string, string> {
  const result: Record<string, string> = {}
  for (const [key, value] of Object.entries(params)) {
    if (value != null) {
      result[key] = value
    }
  }
  return result
}
